import {
  FieldArray,
  FieldArrayItem,
  FieldHint,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  MarkdownInput,
  SwitchCard,
  toast,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { useEffect } from 'react';
import { type Control, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { Agent, AgentInterface, UpdateAgentBehaviorDto, UpdateAgentDto } from '../types';

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  systemPrompt: z.string(),
  initialSalutation: z.boolean(),
  salutationExamples: z.array(z.object({ value: z.string() })),
});

type FormValues = z.infer<typeof schema>;

function buildDefaults(agent: Agent): FormValues {
  return {
    systemPrompt: agent.systemPrompt ?? '',
    initialSalutation: agent.interface.initialSalutation ?? false,
    salutationExamples: (agent.interface.salutationExamples ?? []).map((v) => ({ value: v })),
  };
}

// ─── Salutation examples array editor ────────────────────────────────────────

function SalutationExamplesField({
  control,
  disabled,
}: {
  control: Control<FormValues>;
  disabled?: boolean;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: 'salutationExamples' });
  return (
    <FieldArray
      label="Salutation examples"
      description="The agent picks one at random to open the conversation. Set these defaults and they will be pre-populated in every new release."
      addLabel="Add example"
      onAdd={disabled ? undefined : () => append({ value: '' })}
      itemCount={fields.length}
      disabled={disabled}
    >
      {fields.map((field, index) => (
        <FieldArrayItem
          key={field.id}
          onRemove={disabled ? undefined : () => remove(index)}
          disabled={disabled}
        >
          <FormField
            control={control}
            name={`salutationExamples.${index}.value`}
            render={({ field: f }) => (
              <FormItem>
                <FormControl>
                  <Input {...f} placeholder={`Example ${index + 1}`} disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldArrayItem>
      ))}
    </FieldArray>
  );
}

// ─── PromptTab ────────────────────────────────────────────────────────────────

export function PromptTab({
  agent,
  onSaveBehavior,
  onSaveBase,
  isSaving = false,
  isBehaviorLocked = false,
}: {
  agent: Agent;
  onSaveBehavior?: (dto: UpdateAgentBehaviorDto) => Promise<void>;
  onSaveBase?: (dto: UpdateAgentDto) => Promise<void>;
  isSaving?: boolean;
  isBehaviorLocked?: boolean;
}) {
  const canEdit = Boolean(onSaveBehavior) && Boolean(onSaveBase);
  const canEditPrompt = canEdit && !isBehaviorLocked;

  const form = useForm<FormValues>({
    resolver: canEdit ? zodResolver(schema) : undefined,
    defaultValues: buildDefaults(agent),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when agent id changes
  useEffect(() => {
    form.reset(buildDefaults(agent));
  }, [agent.id]);

  const isDirty = form.formState.isDirty;

  async function onSubmit(data: FormValues) {
    console.log('[PromptTab] save payload', data);
    try {
      if (canEditPrompt && data.systemPrompt !== agent.systemPrompt) {
        await onSaveBehavior?.({ systemPrompt: data.systemPrompt });
      }

      const ifaceHasChanged =
        data.initialSalutation !== (agent.interface.initialSalutation ?? false) ||
        JSON.stringify(data.salutationExamples.map((e) => e.value)) !==
          JSON.stringify(agent.interface.salutationExamples ?? []);

      if (ifaceHasChanged) {
        const updatedInterface: AgentInterface = {
          ...agent.interface,
          initialSalutation: data.initialSalutation,
          salutationExamples: data.salutationExamples.map((e) => e.value).filter(Boolean),
        };
        await onSaveBase?.({ interface: updatedInterface });
      }

      toast.success('Prompt settings saved');
      form.reset(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save prompt settings');
    }
  }

  return (
    <Form {...form}>
      <FormLayout
        className="sm:pt-4"
        actionsPosition="sticky"
        isDirty={canEdit && isDirty}
        cancelAction={{
          label: 'Discard',
          type: 'button',
          onClick: () => form.reset(buildDefaults(agent)),
        }}
        submitAction={{
          label: 'Save changes',
          type: 'button',
          disabled: isSaving,
          onClick: () => void form.handleSubmit(onSubmit)(),
        }}
      >
        <FormSection title="System Prompt" asCard>
          {isBehaviorLocked && (
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/30">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Active deployment running
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  System prompt cannot be changed while there is an active deployment. Deactivate
                  the deployment first.
                </p>
              </div>
            </div>
          )}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">
              Baseline instructions sent to the model at the start of every run. This value is
              snapshotted into each new release you create.
            </p>
          </div>
          <FormField
            control={form.control}
            name="systemPrompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">System Prompt</FormLabel>
                <FormControl>
                  <MarkdownInput
                    value={field.value}
                    onChange={field.onChange}
                    readOnly={!canEditPrompt}
                    placeholder="You are a helpful assistant…"
                    minHeight="320px"
                    expandLabel="System Prompt"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        <FormSection title="Salutation defaults" asCard>
          <p className="text-xs text-muted-foreground">
            Default salutation settings copied into every new release. Each release can override
            these independently.
          </p>
          <FormField
            control={form.control}
            name="initialSalutation"
            render={({ field }) => (
              <SwitchCard
                id="initialSalutation"
                label="Agent greets first"
                description="The agent initiates the conversation with a salutation instead of waiting for the user's first message."
                icon={<MessageSquare className="size-5 text-muted-foreground" />}
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={!canEdit}
              />
            )}
          />
          <SalutationExamplesField control={form.control} disabled={!canEdit} />
          <p className="text-xs text-muted-foreground">
            <FieldHint>
              These defaults are stored on the agent and automatically snapshotted into each new
              release. You can override them per-release on the release detail page.
            </FieldHint>
          </p>
        </FormSection>
      </FormLayout>
    </Form>
  );
}
